import { Check, Info, Minus, Pencil, Plus } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/Components/ui/hover-card";
import PropertyIdentity from "@/Components/Passport/PropertyIdentity";
import FileFieldType from "@/Components/FieldTypes/file";
import ImageFieldType from "@/Components/FieldTypes/image";
import RangeFieldType from "@/Components/FieldTypes/range";
import { hasValue } from "@/lib/utils";
import { DEFAULT_LANGUAGE, resolveValue } from "@/lib/multilingual";
import { normaliseDataElementId } from "@/lib/dataElements";
import { tierLabel } from "@/lib/granularity";
import { dayMonthYear } from "@/lib/serverTime";
import useValueSwap from "./useValueSwap";

/**
 * What a value row says about its property, worked out once for the row and
 * for the editor it opens (ValueField's drawer names the same things):
 *
 * - `displayedValue`: the one translation this view shows (a text-bearing
 *   property stores a translation map; `language`, falling back to the
 *   ontology's `defaultLanguage`, picks from it).
 * - `reported`: the EU registry data element the property maps to, or null.
 *   Absent means the registry never sees it.
 * - `hasStored`: whether there is a value to show (0 and false count).
 * - `editLabel`: "Edit X" or "Add X", the edit control's accessible name.
 */
export function valueRowFacts(property, value, language = null, defaultLanguage = DEFAULT_LANGUAGE) {
    const displayedValue = resolveValue(value, language, defaultLanguage).value;
    const dataElementId = normaliseDataElementId(property.data_element_id);
    const reported = typeof dataElementId === 'string' ? dataElementId : null;
    const hasStored = displayedValue !== undefined && displayedValue !== null && displayedValue !== '';

    return { displayedValue, reported, hasStored, editLabel: `${hasStored ? 'Edit' : 'Add'} ${property.name}` };
}

/**
 * The value as a row shows it, for its type: what goes in the `<dd>`. A
 * component of its own because a swap (useValueSwap) draws it twice, the old
 * value and the new one.
 */
function ValueContent({ property, displayedValue, hasStored, editLabel, disabled, onEdit, dayFirst }) {
    return !hasStored ? (
        disabled ? (
            <span className="text-zinc-400">No value</span>
        ) : (
            <button
                type="button"
                onClick={onEdit}
                aria-label={editLabel}
                className="inline-flex items-center gap-1 rounded-sm text-zinc-400 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/15"
            >
                <Plus className="size-3.5" aria-hidden="true" />
                Add value
            </button>
        )
    ) : property.type === 'boolean' ? (
        (displayedValue === 'true' || displayedValue === true) ? (
            <span className="inline-flex items-center gap-1.5"><Check className="size-3.5 text-emerald-600" aria-hidden="true" />Yes</span>
        ) : (
            <span className="inline-flex items-center gap-1.5 text-zinc-600"><Minus className="size-3.5 text-zinc-400" aria-hidden="true" />No</span>
        )
    ) : property.type === 'file' ? (
        <FileFieldType value={displayedValue} />
    ) : property.type === 'image' ? (
        <ImageFieldType value={displayedValue} />
    ) : property.type === 'tags' && Array.isArray(displayedValue) ? (
        <span className="flex flex-wrap gap-1">
            {displayedValue.map((tag, tagIndex) => (
                <span key={tagIndex} className="inline-flex h-5 items-center rounded bg-zinc-100 px-1.5 text-[11.5px] text-zinc-700">{tag}</span>
            ))}
        </span>
    ) : property.type === 'measurement' ? (
        <span className="whitespace-pre-line tabular-nums">
            {displayedValue} <span className="text-zinc-500">{property.value?.measurement}</span>
        </span>
    ) : property.type === 'range' ? (
        <div className="w-full max-w-sm">
            <RangeFieldType value={displayedValue} measurement={property.value?.measurement} />
        </div>
    ) : property.type === 'date' ? (
        <span className="tabular-nums">{(dayFirst && dayMonthYear(displayedValue)) || new Date(displayedValue).toLocaleDateString()}</span>
    ) : hasValue(displayedValue) ? (
        <span className="whitespace-pre-line">{displayedValue}</span>
    ) : (
        <span className="text-zinc-400">No value</span>
    );
}

/**
 * One property of a passport's value list, as a `<dt>`/`<dd>` row: the name
 * with its identity marker (and EU tag when reported), the value rendered for
 * its type, and the edit affordances. Presentational: it keeps no data (only
 * a swap's animation) and reaches no network. ValueField wraps it with the
 * editing drawer; a
 * marketing vignette renders it bare.
 *
 * - `value`: the property's stored value (`passport.data[id].value`), a
 *   translation map for a text-bearing property.
 * - `reportedTier`: the level's granularity (model/batch/item), which decides
 *   the submission a reported value travels in.
 * - `onEdit()`: asked for by "Add value", the pencil, or a click anywhere on
 *   an editable row.
 * - `disabled`: the released or read-only lock; the row offers no edit.
 * - `children`: rendered last inside the row (ValueField's drawer, whose
 *   portal content bubbles React events back through the row).
 * - `dayFirst`: a date value day first ("11 Sep 2026") instead of in the
 *   browser's locale, for the marketing site's vignettes (1d); off by default.
 * - `swap`: `{ from, onEnd }`, a value just saved by hand. `from` is the
 *   stored value the row showed before; it fades up and out as the current
 *   one fades up into place (useValueSwap), then `onEnd()` is called. Left
 *   out (the default, and the vignettes), a new value just replaces the old.
 * - `className` and `style`: more classes and inline style for the row
 *   (ValueField's wash on a value someone else just changed, useLiveWash).
 * - `live`: what LiveUnit hands the element that is its frame (`{ frame,
 *   badge, footer }`, LiveUnit's function child), when the row is a live
 *   unit (ValueField's field). The row element is the frame, taking its
 *   attributes, classes and style over the row's own, and the value cell
 *   holds the badge (who else is in it) and the footer (what LiveUnit says
 *   under it), so the description list keeps its `<dt>`/`<dd>` rows.
 *   `liveField`: the value cell's `data-live-field`, where the holder's
 *   field mark lands. Neither by default (the vignettes).
 */
export default function ValueRow({
    property,
    value,
    reportedTier = null,
    onEdit,
    disabled = false,
    language = null,
    defaultLanguage = DEFAULT_LANGUAGE,
    children,
    dayFirst = false,
    swap = null,
    className = '',
    style = null,
    live = null,
    liveField = null,
}) {
    const { displayedValue, reported, hasStored, editLabel } = valueRowFacts(property, value, language, defaultLanguage);
    const { ref: frameRef, className: frameClassName = '', style: frameStyle = null, ...frameRest } = live?.frame ?? {};
    const edit = () => onEdit?.();
    const { playing, layered, outgoingRef, incomingRef } = useValueSwap(swap);
    const content = { property, displayedValue, hasStored, editLabel, disabled, onEdit: edit, dayFirst };

    // The whole row opens the editor, so the pencil is not the only target.
    // This is a pointer convenience: keyboards and screen readers keep the
    // pencil (and "Add value"), so the row adds no second tab stop. Anything
    // interactive inside the row — a file link, the info marker — keeps its
    // own click, and a click that ends a text selection is someone copying a
    // value, not asking to edit it.
    const openFromRow = (event) => {
        if (disabled) return;
        // React bubbles events out of portals: a click inside the open drawer
        // (or on its overlay, to close it) arrives here too and must not
        // reopen it. Only the row's own DOM counts.
        if (!event.currentTarget.contains(event.target)) return;
        if (event.target.closest('a, button, input, textarea, select, [role="button"]')) return;
        if (window.getSelection?.()?.toString()) return;
        edit();
    };

    return (
        <div
            {...frameRest}
            ref={frameRef}
            onClick={openFromRow}
            style={live ? { ...style, ...frameStyle } : style}
            className={[
                'group grid min-h-10 grid-cols-[minmax(0,1fr)_28px] items-center gap-x-4 gap-y-1 px-3 py-1.5 transition-colors sm:grid-cols-[minmax(140px,240px)_minmax(0,1fr)_28px]',
                disabled ? '' : 'cursor-pointer hover:bg-zinc-50/70',
                // Only when given: the vignettes' markup stays as it was.
                ...(className ? [className] : []),
                ...(frameClassName ? [frameClassName] : []),
            ].join(' ')}
        >
            <dt className="col-start-1 row-start-1 flex min-w-0 items-center gap-1.5 text-[13px] text-zinc-500">
                <span className="truncate" title={property.name}>{property.name}</span>
                <HoverCard>
                    {/*
                      * One trigger, not two: the property id and the registry
                      * mapping are both "what is this field", and a second
                      * hover target beside the first is a second thing to
                      * discover for no gain.
                      *
                      * A button rather than a bare icon, because a Radix hover
                      * card opens on hover alone — a keyboard user never sees
                      * inside it. The accessible name carries the same facts,
                      * which is what a screen reader announces, and the button
                      * puts the marker in the tab order at all. The info glyph
                      * only shows on row hover or focus; the EU tag, when the
                      * property is reported, always does.
                      */}
                    <HoverCardTrigger asChild>
                        <button
                            type="button"
                            className="inline-flex shrink-0 items-center gap-1 rounded-sm leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 [&:focus-visible>svg]:opacity-100"
                            aria-label={`Property ID ${property.id}`
                                + (reported
                                    ? `. Reported to the EU registry as ${reported}`
                                        + (reportedTier ? `, at ${tierLabel(reportedTier)} granularity` : '')
                                    : '')}
                        >
                            <Info className="size-3.5 text-zinc-400 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
                            {reported && (
                                <span aria-hidden="true" className="inline-flex items-center rounded px-1 text-[10.5px] font-medium leading-4 text-emerald-700 ring-1 ring-inset ring-emerald-600/15 bg-emerald-50">
                                    EU
                                </span>
                            )}
                        </button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-72">
                        <PropertyIdentity
                            propertyId={property.id}
                            dataElementId={reported}
                            tier={reportedTier}
                        />
                    </HoverCardContent>
                </HoverCard>
            </dt>
            <dd
                data-live-field={liveField ?? undefined}
                className={[
                    'col-start-1 row-start-2 min-w-0 text-[13px] text-zinc-950 sm:col-start-2 sm:row-start-1',
                    // Says "this opens" on hover, where the row is the target.
                    !disabled && hasStored ? 'decoration-zinc-300 underline-offset-[3px] group-hover:underline' : '',
                ].join(' ')}
            >
                {live?.badge}
                {layered ? (
                    <div className="grid grid-cols-[minmax(0,1fr)]">
                        {playing && (
                            // The old value, leaving. Hidden from assistive
                            // technology and out of the tab order, so only the
                            // new value is ever read or reached.
                            <div ref={outgoingRef} aria-hidden="true" inert="" className="[grid-area:1/1]">
                                <ValueContent {...content} {...valueRowFacts(property, swap.from, language, defaultLanguage)} />
                            </div>
                        )}
                        <div ref={incomingRef} className="[grid-area:1/1]">
                            <ValueContent {...content} />
                        </div>
                    </div>
                ) : (
                    <ValueContent {...content} />
                )}
                {live?.footer}
            </dd>
            <div className="col-start-2 row-span-2 row-start-1 flex justify-end sm:col-start-3 sm:row-span-1">
                {hasStored && (
                    <button
                        type="button"
                        onClick={edit}
                        disabled={disabled}
                        aria-label={editLabel}
                        className="inline-flex size-7 items-center justify-center rounded-md text-zinc-500 transition hover:bg-zinc-200/60 hover:text-zinc-900 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/15 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 disabled:!opacity-0"
                    >
                        <Pencil className="size-3.5" aria-hidden="true" />
                    </button>
                )}
            </div>
            {children}
        </div>
    );
}
